import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";

const MAX = 4;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

export const Lightbox = ({ photos, start, name, onClose }) => {
  const [i, setI] = useState(start);
  const [z, setZ] = useState({ scale: 1, ox: 50, oy: 50 });
  const touch = useRef({});

  useEffect(() => setZ({ scale: 1, ox: 50, oy: 50 }), [i]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setI(n => (n + 1) % photos.length);
      if (e.key === "ArrowLeft") setI(n => (n - 1 + photos.length) % photos.length);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [photos.length, onClose]);

  const pct = (e, el) => {
    const r = el.getBoundingClientRect();
    return { ox: clamp(((e.clientX - r.left) / r.width) * 100, 0, 100), oy: clamp(((e.clientY - r.top) / r.height) * 100, 0, 100) };
  };
  const toggle = (e) => { const p = pct(e, e.currentTarget); setZ(s => (s.scale > 1 ? { scale: 1, ox: 50, oy: 50 } : { scale: 2.5, ...p })); };

  const onTouchStart = (e) => {
    const t = e.touches;
    touch.current = t.length === 2 ? { d: dist(t), base: z.scale } : { x: t[0].clientX, y: t[0].clientY };
  };
  const onTouchMove = (e) => {
    const t = e.touches, s = touch.current;
    if (t.length === 2 && s.d) { setZ(v => ({ ...v, scale: clamp(s.base * (dist(t) / s.d), 1, MAX) })); return; }
    if (t.length === 1 && z.scale > 1 && s.x !== undefined) {
      const r = e.currentTarget.getBoundingClientRect();
      const dx = ((t[0].clientX - s.x) / r.width) * 100 / z.scale, dy = ((t[0].clientY - s.y) / r.height) * 100 / z.scale;
      touch.current = { x: t[0].clientX, y: t[0].clientY };
      setZ(v => ({ ...v, ox: clamp(v.ox - dx, 0, 100), oy: clamp(v.oy - dy, 0, 100) }));
    }
  };

  const step = (d) => setI(n => (n + d + photos.length) % photos.length);

  return (
    <div className="lightbox" role="dialog" aria-label="Photo zoom" data-testid="lightbox">
      <button type="button" className="lightbox-close" onClick={onClose} aria-label="Close" data-testid="lightbox-close"><X size={22} /></button>
      <div className="lightbox-stage" onClick={toggle} onTouchStart={onTouchStart} onTouchMove={onTouchMove} data-testid="lightbox-stage">
        <img src={photos[i]} alt={`${name} ${i + 1}`} draggable={false} style={{ transform: `scale(${z.scale})`, transformOrigin: `${z.ox}% ${z.oy}%` }} className={z.scale > 1 ? "zoomed" : ""} data-testid="lightbox-image" />
      </div>
      <div className="lightbox-bar">
        {photos.length > 1 && <button type="button" onClick={() => step(-1)} aria-label="Previous photo" data-testid="lightbox-prev"><ChevronLeft size={20} /></button>}
        <span data-testid="lightbox-hint">{z.scale > 1 ? <><ZoomOut size={14} /> Tap to zoom out</> : <><ZoomIn size={14} /> Tap or pinch to zoom</>}{photos.length > 1 && ` · ${i + 1} / ${photos.length}`}</span>
        {photos.length > 1 && <button type="button" onClick={() => step(1)} aria-label="Next photo" data-testid="lightbox-next"><ChevronRight size={20} /></button>}
      </div>
    </div>
  );
};
